import React, { useEffect, useState, useRef, useContext } from "react";

import { toast } from "react-toastify";
import useSound from "use-sound";
import {
	Sparkles,
	Wand2,
	FileText,
	Gauge,
	Copy,
	Loader2,
	X,
	AlertTriangle,
	Clock,
	Zap,
	Tag,
	ArrowRightLeft,
	CheckCircle2,
	XCircle,
	UserCircle,
	History,
	MessageSquare,
	CalendarDays,
	RefreshCw,
	Database,
	Rocket,
	HelpCircle,
	CalendarClock,
	Lightbulb,
	Ban,
	BrainCircuit,
	Plus,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Progress } from "../ui/progress";
import { Skeleton } from "../ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { cn } from "../../lib/utils";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";

const fmtData = (iso) => {
	if (!iso) return "—";
	try {
		const d = new Date(iso);
		return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1)
			.toString()
			.padStart(2, "0")} ${d.getHours().toString().padStart(2, "0")}:${d
			.getMinutes()
			.toString()
			.padStart(2, "0")}`;
	} catch {
		return "—";
	}
};

const NIVEL_COR = {
	alto: "border-destructive/30 bg-destructive/5 text-destructive",
	medio: "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300",
	baixo: "border-sky-500/30 bg-sky-500/5 text-sky-700 dark:text-sky-300",
};

const STATUS_COR = {
	open: "bg-sky-500/10 text-sky-700 dark:text-sky-300",
	pending: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
	closed: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
};

const TIMELINE_COR = {
	indigo: "bg-indigo-500",
	slate: "bg-slate-400",
	emerald: "bg-emerald-500",
	sky: "bg-sky-500",
	rose: "bg-rose-500",
};

const HUMOR = {
	satisfeito: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
	neutro: "bg-slate-500/10 text-slate-600 dark:text-slate-400",
	irritado: "bg-destructive/10 text-destructive",
};

const RISCO = {
	alto: "bg-destructive/10 text-destructive",
	medio: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
	baixo: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
};

const CATEGORIAS_CM = [
	{ value: "attention", label: "Atenção", emoji: "⚠️", desc: "Alertas, restrições, cuidado especial" },
	{ value: "commercial", label: "Comercial", emoji: "💼", desc: "Preços, contratos, negociação" },
	{ value: "preferences", label: "Preferências", emoji: "💖", desc: "Coisas que gosta / não gosta" },
	{ value: "history", label: "Histórico", emoji: "📜", desc: "Anedotas, relacionamento passado" },
	{ value: "profile", label: "Perfil", emoji: "👤", desc: "Dados pessoais relevantes" },
	{ value: "notes", label: "Observações", emoji: "📝", desc: "Geral" },
];
const CATEGORIA_POR_VALOR_CM = Object.fromEntries(CATEGORIAS_CM.map((c) => [c.value, c]));
const MEMORY_VAZIA_CM = {
	id: null,
	category: "notes",
	title: "",
	content: "",
	source: "manual",
};

const iconeDoNome = (nome) => {
	const map = {
		AlertTriangle,
		Clock,
		Zap,
		Tag,
		ArrowRightLeft,
		CheckCircle2,
	};
	return map[nome] || Sparkles;
};

// Painel lateral que acompanha a conversa. Nunca envia nada sozinho: o
// atendente escolhe o que usar.
const AiCopilot = ({ ticketId, onUsarTexto, ultimaRespostaAtendente }) => {
	const { user } = useContext(AuthContext);
	const [aberto, setAberto] = useState(false);
	const [carregando, setCarregando] = useState(null);
	const [sugestoes, setSugestoes] = useState([]);
	const [analise, setAnalise] = useState(null);
	const [resumo, setResumo] = useState(null);
	const [score, setScore] = useState(null);
	const [entities, setEntities] = useState(null);
	const [followup, setFollowup] = useState(null);
	const [iaStatus, setIaStatus] = useState(null);
	const [draft, setDraft] = useState(null);
	const [perguntas, setPerguntas] = useState(null);
	const [compromissos, setCompromissos] = useState([]);

	const [alertas, setAlertas] = useState([]);
	const [coachLoading, setCoachLoading] = useState(false);
	const [alternandoEnableCoach, setAlternandoEnableCoach] = useState(false);

	// RACE FIX volume notificações: ref sempre atualizada a cada render
	// com o valor MAIS NOVO do user.notificationVolume. O playAlertaSonoro
	// (disparado no socket handler, closure antiga de create) lê esta ref
	// NA HORA do play, garantindo 0% = silêncio real mesmo que o listener
	// socket tenha sido criado no paint inicial (closure com valor 50%).
	//
	// BUG || 50: 0 || 50 === 50 (JS || operator converte falsy → default).
	// Então 0% (valor 0 banco) era sobrescrito 50% AQUI TAMBÉM!
	// Checagem explícita undefined/null.
	const notifVolumeBruto =
		user && typeof user?.notificationVolume !== "undefined" && user?.notificationVolume !== null
			? Number(user.notificationVolume)
			: 50;
	const notifVolume01 = Math.max(0, Math.min(1, notifVolumeBruto / 100));
	const notifVolume01Ref = useRef(notifVolume01);
	// Atualiza SEMPRE, sem condicional, durante o render (valor
	// instantaneamente disponível para quem lê a ref agora).
	notifVolume01Ref.current = notifVolume01;

	const [playAlertaSonoro] = useSound(alertSound, { volume: notifVolume01 });

	// -------- Customer Memory (Memória do Cliente) — estado do Dialog --------
	const [cmDialogAberto, setCmDialogAberto] = useState(false);
	const [cmMemoryForm, setCmMemoryForm] = useState(MEMORY_VAZIA_CM);
	const [cmModoEdicao, setCmModoEdicao] = useState(false);
	const [cmSending, setCmSending] = useState(false);
	const abrirCmCriar = () => {
		setCmMemoryForm({ ...MEMORY_VAZIA_CM });
		setCmModoEdicao(false);
		setCmDialogAberto(true);
	};
	const abrirCmEditar = (r) => {
		setCmMemoryForm({
			id: r.id,
			category: r.category,
			title: r.title || "",
			content: r.content || "",
			source: r.source || "manual",
		});
		setCmModoEdicao(true);
		setCmDialogAberto(true);
	};
	const salvarCm = async () => {
		const contatoId = contexto?.cliente?.id ?? null;
		if (!contatoId) {
			toastError(new Error("Contato não identificado neste ticket."));
			return;
		}
		try {
			setCmSending(true);
			const payload = {
				contactId: Number(contatoId),
				category: cmMemoryForm.category,
				title: cmMemoryForm.title.trim() || null,
				content: cmMemoryForm.content.trim(),
				source: cmModoEdicao ? cmMemoryForm.source : "manual",
				status: cmModoEdicao ? undefined : "confirmed",
			};
			if (!payload.content) {
				toastError(new Error("Conteúdo é obrigatório."));
				return;
			}
			if (cmModoEdicao && cmMemoryForm.id) {
				await api.put(`/customer-memory/${cmMemoryForm.id}`, payload);
			} else {
				await api.post(`/customer-memory`, payload);
			}
			setCmDialogAberto(false);
			toast.success(cmModoEdicao ? "Memória atualizada." : "Memória criada.");
			carregarContexto(true);
		} catch (err) {
			toastError(err);
		} finally {
			setCmSending(false);
		}
	};

	const alertasPendentes = alertas.filter((a) => a.status === "pendente").length;

	const coachAtivo = Boolean(
		iaStatus?.ativa &&
		(
			(iaStatus?.provedores || []).some((p) => p.isActive && Array.isArray(p.features) && p.features.includes("coach")) ||
			coachAtivoFallback(iaStatus)
		)
	);

	function coachAtivoFallback(status) {
		if (!status || !Array.isArray(status.provedores) || status.provedores.length === 0) return false;
		const temProvedorAtivo = status.provedores.some((p) => p.isActive === true);
		const todosSemFeatures = status.provedores.every((p) => !Array.isArray(p.features) || p.features.length === 0);
		return temProvedorAtivo && todosSemFeatures;
	}

	const carregarAlertasCoach = async () => {
		if (!ticketId || !coachAtivo) return;
		setCoachLoading(true);
		try {
			const { data } = await api.get(`/tickets/${ticketId}/coach/alerts`, {
				headers: { Accept: "application/json" },
			});
			// Compatibilidade: backend pode retornar array direto OU wrapper { alerts: [...] }
			let arr = [];
			if (Array.isArray(data)) {
				arr = data;
			} else if (data && typeof data === "object") {
				if (Array.isArray(data.alerts)) arr = data.alerts;
				else if (Array.isArray(data.data)) arr = data.data;
			}
			setAlertas(arr);
		} catch (err) {
			setAlertas([]);
			console.warn("[coach] carregar falhou", err);
		} finally {
			setCoachLoading(false);
		}
	};

	const atualizarStatusCoach = async (id, status) => {
		try {
			const { data } = await api.patch(
				`/tickets/${ticketId}/coach/alerts/${id}`,
				{ status },
				{ headers: { Accept: "application/json" } }
			);
			setAlertas((prev) =>
				prev.map((a) => (a.id === id ? { ...a, ...(data || {}), status } : a))
			);
			if (status === "ignorado") {
				setAlertas((prev) => prev.filter((a) => a.id !== id));
			}
		} catch (err) {
			toastError(err);
		}
	};

	const acionarCoachManual = async () => {
		if (!ticketId || !coachAtivo) return;
		setCoachLoading(true);
		try {
			await api.post(
				`/tickets/${ticketId}/coach/trigger`,
				{},
				{ headers: { Accept: "application/json" } }
			);
			toast.success("Coach acionado. Aguarde as sugestões…");
		} catch (err) {
			toastError(err);
		} finally {
			setCoachLoading(false);
		}
	};

	const alternarCoachCliente = async (ligar) => {
		try {
			setAlternandoEnableCoach(true);
			await api.put(
				`/tickets/${ticketId}/coach/enable-contact`,
				{ enable: ligar },
				{ headers: { Accept: "application/json" } }
			);
			setContexto((prev) =>
				prev?.cliente
					? { ...prev, cliente: { ...prev.cliente, enableCoach: ligar } }
					: prev
			);
			toast.success(
				ligar
					? "Coach habilitado para este cliente."
					: "Coach desabilitado para este cliente. (botão ⟳ ainda funciona)"
			);
		} catch (err) {
			toastError(err);
		} finally {
			setAlternandoEnableCoach(false);
		}
	};

	const usarSugestaoCoach = (alerta) => {
		// Prioridade máxima: se o Coach deu um texto PRONTO para o atendente
		// enviar, cola na caixa de mensagem (fecha sheet + toast) independente
		// do CTA. O CTA só é usado se não tiver texto pronto.
		if (typeof alerta.sugestaoDeTexto === "string" && alerta.sugestaoDeTexto.trim().length > 0) {
			try { usar(alerta.sugestaoDeTexto.trim()); } catch { /* no-op */ }
		} else if (alerta.cta === "extract_entities") {
			chamar("extract", `/tickets/${ticketId}/ai/extract`, setEntities);
		} else if (alerta.cta === "suggest_reply") {
			chamar(
				"sugerir",
				`/tickets/${ticketId}/ai/suggest`,
				(d) => setSugestoes(d.sugestoes || [])
			);
		} else if (alerta.cta === "summary") {
			chamar("resumir", `/tickets/${ticketId}/ai/summary`, setResumo);
		} else if (alerta.cta === "analyze_tone") {
			chamar("analisar", `/tickets/${ticketId}/ai/analyze`, setAnalise);
		} else if (alerta.cta === "follow_up") {
			chamar("followup", `/tickets/${ticketId}/ai/followup`, setFollowup);
		} else if (alerta.cta === "tag_contact") {
			toast.info("Atribua a etiqueta sugerida no painel do cliente ao lado →");
		} else if (alerta.cta === "review_process") {
			toast.info("Revise o processo interno para este atendimento.");
		} else {
			// CTA outro ou desconhecido: só marca aceito para auditoria.
			toast.success("Ação registrada pelo Coach.");
		}
		atualizarStatusCoach(alerta.id, "aceito");
	};

	const ignorarAlertaCoach = (id) => {
		atualizarStatusCoach(id, "ignorado");
	};

	const carregarIaStatus = async () => {
		try {
			const { data } = await api.get("/ai/status", {
				headers: { Accept: "application/json" },
			});
			setIaStatus(data);
		} catch (err) {
			setIaStatus({ ativa: false, provedores: [] });
			console.warn("[copiloto] /ai/status não disponível", err);
		}
	};

	// Contexto heurístico OFFLINE: cliente + histórico + quick answers + ações
	const [contexto, setContexto] = useState(null);
	const [loadingCtx, setLoadingCtx] = useState(false);

	const carregarContexto = async (forcar = false) => {
		if (!ticketId) return;
		setLoadingCtx(true);
		try {
			const { data } = await api.get(`/tickets/${ticketId}/ai/context`, {
				params: forcar ? { _: Date.now() } : undefined,
				headers: { Accept: "application/json" },
			});
			setContexto(data);
		} catch (err) {
			setContexto(null);
			// Não exibir toast: LLM key não configurada e erros transitórios de
			// permissão são esperados no primeiro uso. Mostrar só no console.
			console.warn("[copiloto] contexto heurístico não disponível", err);
		} finally {
			setLoadingCtx(false);
		}
	};

	// Ao abrir (e ticketId existir) carrega o contexto 1 vez + status IA.
	useEffect(() => {
		if (aberto && ticketId) {
			Promise.all([carregarContexto(), carregarIaStatus()]);
		}
	}, [aberto, ticketId]);

	// Carrega alertas do Coach quando: sheet aberto, ticket existe, feature coach ativa.
	// Também re-roda quando coachAtivo virar true (iaStatus é assíncrono).
	useEffect(() => {
		if (aberto && ticketId && coachAtivo) {
			carregarAlertasCoach();
		}
	}, [aberto, ticketId, coachAtivo]);

	// Socket IO do Coach: atualiza cards em tempo real + som/toast para alerta P>=4 (PA2).
	useEffect(() => {
		if (!aberto || !ticketId || !coachAtivo) return undefined;

		const socket = openSocket();

		const handler = (evt) => {
			try {
				if (!evt || String(evt.ticketId) !== String(ticketId)) return;

				const { action, alert } = evt;

				if (action === "create" && alert) {
					setAlertas((prev) => {
						if (prev.some((a) => a.id === alert.id)) return prev;
						return [alert, ...prev];
					});

					if (Number(alert.prioridade) >= 4) {
						try {
							// HOWLER BUG PARANOICO FIX (threshold 1%):
							// Relato usuario: '1% sai mega baixo, 0% sai som NORMAL'
							// → bug conhecido da lib use-sound/howler em que ela
							// ignora volume=0 exato e toca 100% default do asset.
							// Solução infalível: abaixo de 1% NÃO chamamos a
							// função playAlertaSonoro nenhuma vez.
							const vRaw = Number(notifVolume01Ref.current);
							const vPlay = Number.isFinite(vRaw)
								? Math.max(0, Math.min(1, vRaw))
								: 0;
							if (vPlay > 0.01) {
								playAlertaSonoro({ volume: vPlay });
							}
						} catch { /* no-op */ }
						toast.warning(
							<div className="flex items-start gap-2">
								<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
								<div className="space-y-0.5">
									<p className="text-xs font-semibold">{alert.titulo || "Alerta do Coach"}</p>
									{alert.descricao ? (
										<p className="text-[11px] opacity-90 line-clamp-2">{alert.descricao}</p>
									) : null}
								</div>
							</div>,
							{ autoClose: 6000, toastId: `coach-${alert.id}` }
						);
					}
				} else if (action === "update" && alert) {
					setAlertas((prev) =>
						prev.map((a) => (a.id === alert.id ? { ...a, ...alert } : a))
					);
					if (alert.status === "ignorado") {
						setAlertas((prev) => prev.filter((a) => a.id !== alert.id));
					}
				} else if (action === "remove") {
					setAlertas((prev) => prev.filter((a) => a.id !== alert?.id));
				}
			} catch (err) {
				console.warn("[coach] socket handler erro", err);
			}
		};

		socket.on("coach:alert", handler);

		return () => {
			try { socket.off("coach:alert", handler); } catch (_) {}
		};
	}, [aberto, ticketId, coachAtivo]);

	const chamar = async (acao, rota, aplicar, body) => {
		setCarregando(acao);
		try {
			const { data } = body != null
				? await api.post(rota, body)
				: await api.post(rota);
			aplicar(data);
		} catch (err) {
			toastError(err);
		}
		setCarregando(null);
	};

	const usar = (texto) => {
		onUsarTexto?.(texto);
		setAberto(false);
		toast.success("Texto colocado na caixa de mensagem.");
	};

	const copiar = async (texto) => {
		try {
			await navigator.clipboard.writeText(texto);
			toast.success("Copiado.");
		} catch (err) {
			toast.error("Não foi possível copiar.");
		}
	};

	const Bloco = ({ titulo, actions, children }) => (
		<section className="rounded-xl border bg-card p-3">
			<div className="mb-2 flex items-center justify-between gap-2">
				<h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
					{titulo}
				</h3>
				<div className="flex items-center gap-1">{actions}</div>
			</div>
			{children}
		</section>
	);

	return (
		<>
			<Button
				variant="ghost"
				size="icon"
				aria-label="Abrir copiloto de IA"
				title={
					alertasPendentes > 0 && !aberto
						? `Copiloto · ${alertasPendentes} alerta(s) do Coach`
						: "Copiloto de IA"
				}
				onClick={() => setAberto(true)}
				className="relative"
			>
				<Sparkles className="h-5 w-5" />
				{coachAtivo && !aberto && alertasPendentes > 0 ? (
					<Badge
						className="pointer-events-none absolute -top-1 -right-1 h-4 min-w-[16px] rounded-full px-1 text-[9px] font-bold border-0 bg-destructive text-destructive-foreground"
						variant="destructive"
					>
						{alertasPendentes > 99 ? "99+" : alertasPendentes}
					</Badge>
				) : null}
			</Button>

			<Sheet open={aberto} onOpenChange={setAberto}>
				<SheetContent
					side="right"
					className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
				>
					<SheetHeader className="border-b p-4">
						<SheetTitle className="flex items-center gap-2 text-base">
							<Sparkles className="h-4 w-4 text-primary" />
							Copiloto
						</SheetTitle>
						<p className="text-xs text-muted-foreground">
							A IA lê a conversa e sugere. Nada é enviado sem você confirmar.
						</p>
					</SheetHeader>

					<div className="flex-1 space-y-3 overflow-y-auto p-4">
						{loadingCtx && !contexto ? (
							<div className="space-y-3">
								<Skeleton className="h-[130px] w-full rounded-xl" />
								<Skeleton className="h-[160px] w-full rounded-xl" />
								<Skeleton className="h-[180px] w-full rounded-xl" />
							</div>
						) : null}

						{/* AÇÕES IA (condicional ao status do provedor) — NO TOPO */}
						{(() => {
							const provedorAtivo = iaStatus?.provedores?.find((p) => p.isActive);
							const iaAtiva = Boolean(iaStatus?.ativa && provedorAtivo);

							return (
								<Bloco titulo="Copiloto IA" actions={
									iaAtiva ? (
										<Badge className="h-5 rounded-full px-2 py-0 text-[10px] font-semibold border-emerald-200 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30">
											<span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 align-middle" />
											IA ativa
										</Badge>
									) : (
										<Badge variant="outline" className="h-5 rounded-full px-2 py-0 text-[10px] border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30">
											Requer chave LLM
										</Badge>
									)
								}>
									<div className="space-y-3">
										{/* 1) Sugerir */}
										<div>
											<p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/90">
												Sugerir
											</p>
											<div className="grid grid-cols-2 gap-1.5">
												{[
													{
														acao: "sugerir",
														rotulo: "3 respostas curtas",
														icone: Wand2,
														rota: `/tickets/${ticketId}/ai/suggest`,
														aplicar: (d) => setSugestoes(d.sugestoes || []),
													},
													{
														acao: "draft",
														rotulo: "1 rascunho detalhado",
														icone: FileText,
														rota: `/tickets/${ticketId}/ai/draft`,
														aplicar: setDraft,
													},
													{
														acao: "followup",
														rotulo: "Follow-up",
														icone: Rocket,
														rota: `/tickets/${ticketId}/ai/followup`,
														aplicar: setFollowup,
													},
													{
														acao: "questions",
														rotulo: "Perguntas a fazer",
														icone: HelpCircle,
														rota: `/tickets/${ticketId}/ai/questions`,
														aplicar: setPerguntas,
													},
												].map((item) => (
													<Button
														key={item.acao}
														variant="outline"
														size="sm"
														className="h-auto flex-col gap-1 py-2"
														disabled={!iaAtiva || Boolean(carregando)}
														onClick={() => chamar(item.acao, item.rota, item.aplicar)}
													>
														{carregando === item.acao ? (
															<Loader2 className="h-4 w-4 animate-spin" />
														) : (
															<item.icone className="h-4 w-4" />
														)}
														<span className="text-[11px] leading-tight text-center">{item.rotulo}</span>
													</Button>
												))}
											</div>
										</div>

										{/* 2) Analisar */}
										<div>
											<p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/90">
												Analisar
											</p>
											<div className="grid grid-cols-2 gap-1.5">
												{[
													{
														acao: "resumir",
														rotulo: "Resumo",
														icone: FileText,
														rota: `/tickets/${ticketId}/ai/summary`,
														aplicar: setResumo,
													},
													{
														acao: "analisar",
														rotulo: "Análise",
														icone: Gauge,
														rota: `/tickets/${ticketId}/ai/analyze`,
														aplicar: setAnalise,
													},
													{
														acao: "extract",
														rotulo: "Dados extraídos",
														icone: Database,
														rota: `/tickets/${ticketId}/ai/extract`,
														aplicar: setEntities,
													},
													{
														acao: "commitments",
														rotulo: "Compromissos & prazos",
														icone: CalendarClock,
														rota: `/tickets/${ticketId}/ai/commitments`,
														aplicar: (d) => setCompromissos(d?.itens || []),
													},
												].map((item) => (
													<Button
														key={item.acao}
														variant="outline"
														size="sm"
														className="h-auto flex-col gap-1 py-2"
														disabled={!iaAtiva || Boolean(carregando)}
														onClick={() => chamar(item.acao, item.rota, item.aplicar)}
													>
														{carregando === item.acao ? (
															<Loader2 className="h-4 w-4 animate-spin" />
														) : (
															<item.icone className="h-4 w-4" />
														)}
														<span className="text-[11px] leading-tight text-center">{item.rotulo}</span>
													</Button>
												))}
											</div>
										</div>

										{!iaAtiva && iaStatus && (
											<p className="pt-1 text-[11px] text-muted-foreground">
												Configure um provedor de IA em{" "}
												<span className="font-semibold">Painel &rarr; Configurações &rarr; IA</span>{" "}
												para ativar as ações acima.
											</p>
										)}
									</div>
								</Bloco>
						);
					})()}

					{coachAtivo ? (() => {
					const escopoCoach = contexto?.coach?.scope === "selected" ? "selected" : "all";
					const clienteHabilitadoCoach = Boolean(contexto?.cliente?.enableCoach);
					const podeAlterarEnableCoachNoCopiloto = escopoCoach !== "selected";
					const coachEstaDisponivelCliente = escopoCoach !== "selected" || clienteHabilitadoCoach;

					const actionsCoach = (() => {
						if (!coachEstaDisponivelCliente) return null;
						if (!podeAlterarEnableCoachNoCopiloto) {
							return (
								<div className="flex items-center gap-1.5">
									{alertasPendentes > 0 ? (
										<Badge className="h-5 rounded-full px-2 py-0 text-[10px] font-semibold border-destructive/30 bg-destructive/10 text-destructive">
											{alertasPendentes} pendente{alertasPendentes > 1 ? "s" : ""}
										</Badge>
									) : null}
									<Button
										variant="ghost"
										size="icon"
										className="h-7 w-7"
										title="Acionar Coach agora"
										onClick={acionarCoachManual}
										disabled={Boolean(coachLoading)}
									>
										{coachLoading ? (
											<Loader2 className="h-3.5 w-3.5 animate-spin" />
										) : (
											<RefreshCw className="h-3.5 w-3.5" />
										)}
									</Button>
								</div>
							);
						}
						return (
							<div className="flex items-center gap-1.5 w-full">
								<div className="flex items-center gap-2 mr-auto pr-2">
									<Label htmlFor="ai-copilot-enable-coach" className="text-[11px] font-medium leading-tight whitespace-nowrap">
										Habilitado neste cliente
									</Label>
									<Switch
										id="ai-copilot-enable-coach"
										checked={Boolean(contexto?.cliente?.enableCoach)}
										disabled={Boolean(coachLoading) || alternandoEnableCoach || !contexto?.cliente}
										onCheckedChange={(valor) => alternarCoachCliente(valor)}
									/>
								</div>
								{alertasPendentes > 0 ? (
									<Badge className="h-5 rounded-full px-2 py-0 text-[10px] font-semibold border-destructive/30 bg-destructive/10 text-destructive">
										{alertasPendentes} pendente{alertasPendentes > 1 ? "s" : ""}
									</Badge>
								) : null}
								<Button
									variant="ghost"
									size="icon"
									className="h-7 w-7"
									title="Acionar Coach agora"
									onClick={acionarCoachManual}
									disabled={Boolean(coachLoading)}
								>
									{coachLoading ? (
										<Loader2 className="h-3.5 w-3.5 animate-spin" />
									) : (
										<RefreshCw className="h-3.5 w-3.5" />
									)}
								</Button>
							</div>
						);
					})();

					return (
						<Bloco titulo="🎓 Coach IA" actions={actionsCoach}>
							{!coachEstaDisponivelCliente ? (
								<div className="flex flex-col items-center justify-center py-6 gap-1 text-center text-muted-foreground px-2">
									<div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted/40">
										<Ban className="h-4 w-4 text-muted-foreground opacity-70" />
									</div>
									<p className="text-[11px] font-medium text-foreground/80">Coach não disponível</p>
									<p className="text-[10px] leading-relaxed max-w-[260px]">
										Administrador definiu que o Coach automático está ativo apenas em contatos marcados. Este cliente não está na lista. Contate o administrador se precisar.
									</p>
								</div>
							) : (
								<>
									{podeAlterarEnableCoachNoCopiloto ? (
										<p className="mb-3 rounded-md bg-sky-500/5 border border-sky-500/20 p-2 text-[11px] leading-relaxed text-muted-foreground">
											Quando <span className="font-medium text-foreground/90">"Apenas contatos selecionados"</span> está ativo em <span className="font-medium text-foreground/90">Talk.Ai › Coach IA</span>, o Coach automático SÓ roda para clientes marcados. O botão <span className="font-medium text-foreground/90">⟳ Rodar Coach agora</span> SEMPRE funciona para qualquer cliente, independente desta marcação e independente do limite diário.
										</p>
									) : null}
									{coachLoading && alertas.length === 0 ? (
										<div className="space-y-2">
											<Skeleton className="h-[72px] w-full rounded-lg" />
											<Skeleton className="h-[72px] w-full rounded-lg" />
										</div>
									) : alertasPendentes === 0 ? (
										<div className="flex flex-col items-center justify-center py-6 gap-2 text-center">
											<div className="flex h-10 w-10 items-center justify-center rounded-full bg-sky-500/10">
												<Lightbulb className="h-5 w-5 text-sky-600 dark:text-sky-400" />
											</div>
											<div className="space-y-0.5">
												<p className="text-xs font-semibold text-foreground">Coach observando</p>
												<p className="text-[11px] text-muted-foreground leading-relaxed max-w-[240px]">
													Enquanto você conversa, o Coach analisa a conversa e sugere ações aqui.
													Clique no ⟳ acima para acionar agora.
												</p>
											</div>
										</div>
									) : (
										<ul className="space-y-2">
											{alertas.map((alerta) => {
												const ehAlerta = alerta.tipo === "alerta" || Number(alerta.prioridade) >= 3;
												const Icone = ehAlerta ? AlertTriangle : Lightbulb;
												const corCard = ehAlerta
													? "border-destructive/30 bg-destructive/[0.04]"
													: "border-sky-500/30 bg-sky-500/[0.04]";
												const corBadge = ehAlerta
													? "border-destructive/30 bg-destructive/10 text-destructive"
													: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400";
												const corIcone = ehAlerta ? "text-destructive" : "text-sky-600 dark:text-sky-400";

												return (
													<li
														key={alerta.id}
														className={cn(
															"rounded-lg border p-2.5 space-y-2 transition-opacity",
															corCard,
															alerta.status === "aceito" ? "opacity-60" : ""
														)}
													>
														<div className="flex items-start gap-2">
															<span className={cn(
																"mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white/70 dark:bg-black/20",
																corIcone
															)}>
																<Icone className="h-3.5 w-3.5" />
															</span>
															<div className="min-w-0 flex-1 space-y-1">
																<div className="flex flex-wrap items-center gap-1">
																	<p className="text-xs font-semibold leading-tight text-foreground">
																		{alerta.titulo || "Sugestão do Coach"}
																	</p>
																	{typeof alerta.prioridade === "number" && (
																		<Badge variant="outline" className={cn(
																			"h-4 px-1.5 py-0 text-[9px] font-bold",
																			alerta.prioridade >= 4
																				? "border-destructive/30 bg-destructive/10 text-destructive"
																				: corBadge
																		)}>
																			P{alerta.prioridade}
																		</Badge>
																	)}
																	{alerta.triggerSource && (
																		<Badge variant="outline" className="h-4 px-1.5 py-0 text-[9px] border-slate-300 text-slate-500">
																			{alerta.triggerSource === "agent_message" ? "atendente" :
																			 alerta.triggerSource === "client_message" ? "cliente" :
																			 alerta.triggerSource === "manual" ? "manual" : alerta.triggerSource}
																		</Badge>
																	)}
																</div>

																{alerta.descricao ? (
																	<p className="text-[11px] leading-relaxed text-muted-foreground">
																		{alerta.descricao}
																	</p>
																) : null}

																{alerta.sugestaoDeTexto ? (
																	<div className="rounded-md border bg-white/50 dark:bg-black/10 px-2 py-1.5 mt-1">
																		<p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/80 mb-0.5">
																			Sugestão de resposta
																		</p>
																		<p className="whitespace-pre-line text-[12px] leading-relaxed text-foreground line-clamp-4">
																			{alerta.sugestaoDeTexto}
																		</p>
																	</div>
																) : null}

																<div className="flex items-center justify-between pt-0.5">
																	<span className="text-[10px] text-muted-foreground">
																		{fmtData(alerta.createdAt)}
																	</span>
																	<div className="flex items-center gap-1">
																		{alerta.status === "aceito" ? (
																			<Badge className="h-5 px-2 py-0 text-[10px] bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200/50">
																				Usado
																			</Badge>
																		) : (
																			<>
																				<Button
																					size="sm"
																					className={cn(
																						"h-7 px-2.5 text-[11px]",
																						ehAlerta
																							? "bg-destructive hover:bg-destructive/90 text-destructive-foreground"
																							: "bg-sky-600 hover:bg-sky-700 text-white"
																					)}
																					onClick={() => usarSugestaoCoach(alerta)}
																				>
																					{alerta.cta === "suggest_reply" || alerta.sugestaoDeTexto ? "Usar" : "Aplicar"}
																				</Button>
																				<Button
																					variant="ghost"
																					size="sm"
																					className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground"
																					onClick={() => ignorarAlertaCoach(alerta.id)}
																				>
																					Ignorar
																				</Button>
																			</>
																		)}
																	</div>
																</div>
															</div>
														</div>
													</li>
												);
											})}
										</ul>
									)}
								</>
							)}
						</Bloco>
					);
				})() : null}

					{/* sugestões de resposta */}
						{sugestoes.length > 0 && (
							<Bloco titulo="Respostas sugeridas (IA)">
								<ul className="space-y-2">
									{sugestoes.map((texto, indice) => (
										<li
											key={indice}
											className="rounded-lg border bg-background p-2.5"
										>
											<p className="whitespace-pre-line text-sm leading-relaxed">
												{texto}
											</p>
											<div className="mt-2 flex gap-1">
												<Button
													size="sm"
													className="h-7 text-xs"
													onClick={() => usar(texto)}
												>
													Usar
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-7 w-7"
													aria-label="Copiar"
													onClick={() => copiar(texto)}
												>
													<Copy className="h-3.5 w-3.5" />
												</Button>
											</div>
										</li>
									))}
								</ul>
							</Bloco>
						)}

						{/* análise */}
						{analise && (
							<Bloco titulo="Leitura da conversa">
								<div className="flex flex-wrap gap-1.5">
									{analise.humor && (
										<span
											className={cn(
												"rounded-full px-2 py-0.5 text-[11px] font-medium",
												HUMOR[analise.humor] || HUMOR.neutro
											)}
										>
											Cliente {analise.humor}
										</span>
									)}
									{analise.urgencia && (
										<span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
											Urgência {analise.urgencia}
										</span>
									)}
									{analise.riscoDeCancelamento && (
										<span
											className={cn(
												"rounded-full px-2 py-0.5 text-[11px] font-medium",
												RISCO[analise.riscoDeCancelamento] || RISCO.baixo
											)}
										>
											Risco de cancelar: {analise.riscoDeCancelamento}
										</span>
									)}
									{analise.oportunidadeDeVenda === "sim" && (
										<span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
											Oportunidade de venda
										</span>
									)}
								</div>

								{analise.resumoCurto && (
									<p className="mt-2 text-xs leading-relaxed text-muted-foreground">
										{analise.resumoCurto}
									</p>
								)}

								{analise.etiquetasSugeridas?.length > 0 && (
									<p className="mt-2 text-xs">
										<span className="text-muted-foreground">
											Etiquetas sugeridas:{" "}
										</span>
										{analise.etiquetasSugeridas.join(", ")}
									</p>
								)}

								{analise.filaSugerida && (
									<p className="mt-1 text-xs">
										<span className="text-muted-foreground">
											Fila sugerida:{" "}
										</span>
										{analise.filaSugerida}
									</p>
								)}
							</Bloco>
						)}

						{/* resumo */}
						{resumo && (
							<Bloco titulo="Resumo">
								<p className="whitespace-pre-line text-sm leading-relaxed">
									{resumo.resumo}
								</p>

								<dl className="mt-3 space-y-1.5 text-xs">
									{[
										["Motivo do contato", resumo.motivoDoContato],
										["Solução aplicada", resumo.solucaoAplicada],
										["Sentimento", resumo.sentimento],
										["Volta a comprar?", resumo.probabilidadeDeRetorno],
									]
										.filter(([, valor]) => valor)
										.map(([rotulo, valor]) => (
											<div key={rotulo}>
												<dt className="text-muted-foreground">{rotulo}</dt>
												<dd>{valor}</dd>
											</div>
										))}
								</dl>

								{resumo.proximosPassos?.length > 0 && (
									<div className="mt-3">
										<p className="text-xs text-muted-foreground">
											Próximos passos
										</p>
										<ul className="mt-1 list-inside list-disc space-y-0.5 text-xs">
											{resumo.proximosPassos.map((passo, i) => (
												<li key={i}>{passo}</li>
											))}
										</ul>
									</div>
								)}

								<Button
									variant="outline"
									size="sm"
									className="mt-3 h-7 w-full text-xs"
									onClick={() => copiar(resumo.resumo)}
								>
									<Copy className="h-3.5 w-3.5" />
									Copiar resumo
								</Button>
							</Bloco>
						)}

						{/* score */}
						{score && (
							<Bloco titulo="Nota do atendimento">
								<div className="flex items-baseline gap-2">
									<span className="text-3xl font-semibold tabular-nums">
										{score.notaFinal ?? "—"}
									</span>
									<span className="text-sm text-muted-foreground">/ 10</span>
								</div>

								<div className="mt-3 space-y-1.5">
									{[
										["Tempo de resposta", score.tempoDeResposta],
										["Educação", score.educacao],
										["Clareza", score.clareza],
										["Objetividade", score.objetividade],
										["Empatia", score.empatia],
									]
										.filter(([, nota]) => typeof nota === "number")
										.map(([rotulo, nota]) => (
											<div key={rotulo}>
												<div className="flex justify-between text-[11px]">
													<span className="text-muted-foreground">
														{rotulo}
													</span>
													<span className="tabular-nums">{nota}</span>
												</div>
												<div className="mt-0.5 h-1 overflow-hidden rounded-full bg-muted">
													<div
														className={cn(
															"h-full rounded-full",
															nota >= 7
																? "bg-emerald-500"
																: nota >= 5
																? "bg-amber-500"
																: "bg-destructive"
														)}
														style={{ width: `${(nota / 10) * 100}%` }}
													/>
												</div>
											</div>
										))}
								</div>

								{score.pontosAMelhorar?.length > 0 && (
									<div className="mt-3">
										<p className="text-xs text-muted-foreground">
											Pontos a melhorar
										</p>
										<ul className="mt-1 list-inside list-disc space-y-0.5 text-xs">
											{score.pontosAMelhorar.map((ponto, i) => (
												<li key={i}>{ponto}</li>
											))}
										</ul>
									</div>
								)}
							</Bloco>
						)}

						{/* follow-up */}
						{followup?.sugestao && (
							<Bloco titulo="Mensagem de follow-up" actions={
								<Badge variant="outline" className="h-5 rounded-full px-2 py-0 text-[10px]">
									Tom: {followup.tom || "—"}
								</Badge>
							}>
								<p className="whitespace-pre-line text-sm leading-relaxed">
									{followup.sugestao}
								</p>
								<div className="mt-2 flex gap-1">
									<Button
										size="sm"
										className="h-7 text-xs"
										onClick={() => usar(followup.sugestao)}
									>
										Usar
									</Button>
									<Button
										variant="ghost"
										size="icon"
										className="h-7 w-7"
										aria-label="Copiar"
										onClick={() => copiar(followup.sugestao)}
									>
										<Copy className="h-3.5 w-3.5" />
									</Button>
								</div>
							</Bloco>
						)}

						{/* rascunho detalhado */}
						{draft?.rascunho && (
							<Bloco titulo="Rascunho detalhado">
								<p className="whitespace-pre-line text-sm leading-relaxed">
									{draft.rascunho}
								</p>
								<div className="mt-2 flex gap-1">
									<Button
										size="sm"
										className="h-7 text-xs"
										onClick={() => usar(draft.rascunho)}
									>
										Usar
									</Button>
									<Button
										variant="ghost"
										size="icon"
										className="h-7 w-7"
										aria-label="Copiar"
										onClick={() => copiar(draft.rascunho)}
									>
										<Copy className="h-3.5 w-3.5" />
									</Button>
								</div>
							</Bloco>
						)}

						{/* perguntas a fazer */}
						{perguntas?.perguntas?.length > 0 && (
							<Bloco titulo="Perguntas a fazer" actions={
								perguntas.motivo ? (
									<span className="text-[10px] text-muted-foreground truncate max-w-[180px]">
										{perguntas.motivo}
									</span>
								) : null
							}>
								<ul className="space-y-1.5">
									{perguntas.perguntas.map((p, i) => (
										<li
											key={i}
											className="rounded-lg border bg-background px-2.5 py-2 text-sm leading-relaxed"
										>
											<span className="mr-1.5 text-muted-foreground text-[11px] font-semibold">
												Q{i + 1}.
											</span>
											{p}
										</li>
									))}
								</ul>
							</Bloco>
						)}

						{/* entidades extraídas */}
						{entities && Object.values(entities).some((v) => Array.isArray(v) ? v.length > 0 : v != null && v !== "") && (
							<Bloco titulo="Entidades extraídas da conversa">
								<dl className="grid grid-cols-1 gap-1.5 text-xs">
									{[
										["Nome completo", entities.nomeCompleto],
										["CPF/CNPJ", entities.cpfCnpj],
										["Telefone", entities.telefone],
										["E-mail", entities.email],
										["CEP", entities.cep],
										["Endereço", entities.endereco],
										["Garantia", entities.garantia],
										["Cancelamento / estorno", entities.cancelamentoEstorno],
										["Outras informações", entities.outrasInformacoes],
									]
										.filter(([, v]) => v != null && v !== "")
										.map(([k, v]) => (
											<div key={k} className="flex gap-2 rounded-lg border bg-background px-2 py-1.5">
												<dt className="w-28 shrink-0 text-muted-foreground">{k}</dt>
												<dd className="min-w-0 flex-1 break-all">{String(v)}</dd>
											</div>
										))}

									{entities.produtos?.length > 0 && (
										<div className="rounded-lg border bg-background px-2 py-1.5">
											<div className="mb-1 flex items-center gap-2">
												<span className="w-28 shrink-0 text-muted-foreground">Produtos</span>
												<div className="flex flex-wrap gap-1">
													{entities.produtos.map((p, i) => (
														<Badge key={i} variant="secondary" className="text-[10px] h-5">{p}</Badge>
													))}
												</div>
											</div>
										</div>
									)}

									{entities.valores?.length > 0 && (
										<div className="rounded-lg border bg-background px-2 py-1.5">
											<div className="mb-1 flex items-center gap-2">
												<span className="w-28 shrink-0 text-muted-foreground">Valores (R$)</span>
												<div className="flex flex-wrap gap-1">
													{entities.valores.map((v, i) => (
														<Badge key={i} className="text-[10px] h-5 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200/50">{v}</Badge>
													))}
												</div>
											</div>
										</div>
									)}

									{entities.pedidoNfProtocolo?.length > 0 && (
										<div className="rounded-lg border bg-background px-2 py-1.5">
											<div className="mb-1 flex items-center gap-2">
												<span className="w-28 shrink-0 text-muted-foreground">Pedido / NF</span>
												<div className="flex flex-wrap gap-1">
													{entities.pedidoNfProtocolo.map((p, i) => (
														<Badge key={i} variant="outline" className="text-[10px] h-5 font-mono">{p}</Badge>
													))}
												</div>
											</div>
										</div>
									)}

									{entities.datas?.length > 0 && (
										<div className="rounded-lg border bg-background px-2 py-1.5">
											<div className="mb-1 flex items-center gap-2">
												<span className="w-28 shrink-0 text-muted-foreground">Datas</span>
												<div className="flex flex-wrap gap-1">
													{entities.datas.map((d, i) => (
														<Badge key={i} variant="outline" className="text-[10px] h-5">{d}</Badge>
													))}
												</div>
											</div>
										</div>
									)}

									{entities.prazosMencionados?.length > 0 && (
										<div className="rounded-lg border bg-background px-2 py-1.5">
											<div className="mb-1 flex items-center gap-2">
												<span className="w-28 shrink-0 text-muted-foreground">Prazos</span>
												<div className="flex flex-wrap gap-1">
													{entities.prazosMencionados.map((p, i) => (
														<Badge key={i} className="text-[10px] h-5 bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-200/50">{p}</Badge>
													))}
												</div>
											</div>
										</div>
									)}
								</dl>
							</Bloco>
						)}

						{/* compromissos & prazos */}
						{compromissos.length > 0 && (
							<Bloco titulo="Compromissos & prazos">
								<ul className="space-y-1.5">
									{compromissos.map((c, i) => (
										<li
											key={i}
											className="rounded-lg border bg-background px-2.5 py-2"
										>
											<div className="flex flex-wrap items-center gap-1.5 mb-0.5">
												<Badge
													variant="outline"
													className={cn(
														"h-5 px-2 py-0 text-[10px] font-medium",
														c.tipo === "prazo" && "border-sky-200/60 bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-400",
														c.tipo === "compromisso" && "border-indigo-200/60 bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-400",
														c.tipo === "retorno" && "border-amber-200/60 bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
														c.tipo === "dado_faltante" && "border-rose-200/60 bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400"
													)}
												>
													{c.tipo || "—"}
												</Badge>
												{c.quem && (
													<Badge variant="outline" className="h-5 px-2 py-0 text-[10px]">
														{c.quem}
													</Badge>
												)}
												{c.status && (
													<Badge
														className={cn(
															"h-5 px-2 py-0 text-[10px]",
															c.status === "concluido" && "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200/50",
															c.status === "pendente" && "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200/50",
															c.status === "duvida" && "bg-slate-500/10 text-slate-700 dark:text-slate-400 border-slate-200/50"
														)}
													>
														{c.status}
													</Badge>
												)}
												{c.quando && (
													<span className="ml-auto text-[10px] text-muted-foreground flex items-center gap-1">
														<CalendarDays className="h-3 w-3" />
														{c.quando}
													</span>
												)}
											</div>
											<p className="text-xs leading-relaxed">
												{c.descricao}
											</p>
										</li>
									))}
								</ul>
							</Bloco>
						)}

						{/* CONTEXTO MEMÓRIA DO CLIENTE (Customer Memory) */}
						{(() => {
							const cm = contexto?.customerMemory;
							const rows = Array.isArray(cm?.rows) ? cm.rows : [];
							const habilitada = Boolean(cm?.enabled);
							const semContato = !contexto?.cliente?.id;
							const mostrar = (habilitada && rows.length > 0) || (habilitada && !semContato);
							if (!mostrar) return null;

							const categoriaLabel = {
								attention: { label: "Atenção", emoji: "⚠️", cor: "border-rose-200/60 bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400" },
								commercial: { label: "Comercial", emoji: "💼", cor: "border-indigo-200/60 bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-400" },
								preferences: { label: "Preferências", emoji: "💖", cor: "border-pink-200/60 bg-pink-50 text-pink-700 dark:bg-pink-500/10 dark:text-pink-400" },
								history: { label: "Histórico", emoji: "📜", cor: "border-sky-200/60 bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-400" },
								profile: { label: "Perfil", emoji: "👤", cor: "border-violet-200/60 bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-400" },
								notes: { label: "Observações", emoji: "📝", cor: "border-amber-200/60 bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" },
							};
							const fonteEmoji = { manual: "🖐️", ai: "🤖", system: "🔧", import: "⬆️" };
							const confirmarMemoria = async (id) => {
								try {
									await api.post(`/customer-memory/${id}/confirm`);
									toast("Memória confirmada ✔️");
									carregarContexto(true);
								} catch (e) {
									toastError(e);
								}
							};
							const rejeitarMemoria = async (id) => {
								try {
									await api.post(`/customer-memory/${id}/reject`);
									toast("Memória rejeitada.");
									carregarContexto(true);
								} catch (e) {
									toastError(e);
								}
							};
							const pendentes = rows.filter((r) => r.status === "pending");
							const confirmarTodasPendentes = async () => {
								try {
									await Promise.all(
										pendentes.map((r) => api.post(`/customer-memory/${r.id}/confirm`))
									);
									toast(`${pendentes.length} sugestões confirmadas ✔️`);
									carregarContexto(true);
								} catch (e) {
									toastError(e);
								}
							};
							return (
								<>
								<Bloco
									titulo="🧠 Contexto do cliente"
									actions={
										<div className="flex items-center gap-1.5">
											<Badge variant="outline" className="h-5 rounded-full px-2 py-0 text-[10px] font-normal">
												{cm.total ?? rows.length} registro(s)
											</Badge>
											{cm.pending > 0 && (
												<Badge
													variant="outline"
													className="h-5 rounded-full border-amber-200/60 bg-amber-50 px-2 py-0 text-[10px] font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400"
												>
													⚠️ {cm.pending} pendente(s)
												</Badge>
											)}
											{!cm.inAiContext && (
												<Badge
													variant="outline"
													className="h-5 rounded-full border-slate-200/60 bg-slate-50 px-2 py-0 text-[10px] text-slate-600 dark:bg-slate-500/10 dark:text-slate-400"
													title="A memória deste cliente não está sendo injetada nas respostas de IA."
												>
													desativada no IA
												</Badge>
											)}
											{pendentes.length > 0 && (
												<Button
													size="sm"
													variant="ghost"
													className="h-6 px-2 text-[11px]"
													onClick={confirmarTodasPendentes}
												>
													Confirmar {pendentes.length}
												</Button>
											)}
											<Button
												size="sm"
												variant="default"
												className="h-6 px-2 text-[11px]"
												onClick={abrirCmCriar}
												disabled={semContato}
												title={semContato ? "Contato não identificado neste ticket." : "Adicionar nova memória"}
											>
												<Plus className="mr-1 h-3 w-3" />
												Adicionar
											</Button>
										</div>
									}
								>
									{rows.length === 0 ? (
										<div className="rounded-lg border border-dashed bg-background/40 px-3 py-4 text-center">
											<p className="text-[11px] text-muted-foreground">
												Nenhuma memória cadastrada ainda. Clique em{" "}
												<strong className="text-foreground">Adicionar</strong> acima para
												registrar algo importante sobre este cliente (ex: alergia,
												preferência de canal, etc.).
											</p>
										</div>
									) : (
									<ul className="space-y-1.5">
										{rows.map((r) => {
											const cat = categoriaLabel[r.category] || categoriaLabel.notes;
											const isPending = r.status === "pending";
											return (
												<li
													key={r.id}
													className={cn(
														"rounded-lg border bg-background px-2.5 py-2",
														isPending && "border-amber-200/60 bg-amber-50/30 dark:bg-amber-500/5"
													)}
												>
													<div className="flex flex-wrap items-center gap-1.5 mb-1">
														<Badge variant="outline" className={cn("h-5 rounded-full border px-2 py-0 text-[10px] font-medium", cat.cor)}>
															{cat.emoji} {cat.label}
														</Badge>
														{typeof fonteEmoji[r.source] === "string" && (
															<Badge variant="secondary" className="h-5 rounded-full px-2 py-0 text-[10px]">
																{fonteEmoji[r.source]} {r.source}
															</Badge>
														)}
														{isPending ? (
															<Badge
																variant="outline"
																className="h-5 rounded-full border-amber-200/60 bg-amber-50 px-2 py-0 text-[10px] font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400"
															>
																<AlertTriangle className="mr-1 h-3 w-3 inline -mt-0.5" />
																Sugestão
															</Badge>
														) : null}
														<span className="ml-auto text-[10px] tabular-nums text-muted-foreground">
															{fmtData(r.createdAt)}
														</span>
													</div>
													{r.title && <p className="mb-0.5 text-[11px] font-semibold">{r.title}</p>}
													<p className="whitespace-pre-line text-xs leading-relaxed">{r.content}</p>
													{isPending && (
														<div className="mt-1.5 flex items-center gap-1">
															<Button
																size="sm"
																variant="ghost"
																className="h-6 px-2 text-[11px] text-emerald-700 dark:text-emerald-400"
																onClick={() => confirmarMemoria(r.id)}
															>
																<CheckCircle2 className="mr-1 h-3 w-3" />
																Confirmar
															</Button>
															<Button
																size="sm"
																variant="ghost"
																className="h-6 px-2 text-[11px] text-rose-700 dark:text-rose-400"
																onClick={() => rejeitarMemoria(r.id)}
															>
																<XCircle className="mr-1 h-3 w-3" />
																Rejeitar
															</Button>
														</div>
													)}
													{!isPending && (
														<div className="mt-1.5 flex items-center justify-end gap-1 opacity-0 transition-opacity hover:opacity-100 focus-within:opacity-100">
															<Button
																size="sm"
																variant="ghost"
																className="h-6 px-2 text-[11px] text-muted-foreground"
																onClick={() => abrirCmEditar(r)}
															>
																Editar
															</Button>
														</div>
													)}
												</li>
											);
										})}
									</ul>
									)}
								</Bloco>
								<Dialog open={cmDialogAberto} onOpenChange={setCmDialogAberto}>
									<DialogContent>
										<DialogHeader>
											<DialogTitle>
												{cmModoEdicao ? "Editar memória" : "Nova memória do cliente"}
											</DialogTitle>
											<DialogDescription>
												Esta informação ficará disponível nos atendimentos deste cliente,
												nos copilot e nas perguntas do Perabit Intelligence.
											</DialogDescription>
										</DialogHeader>
										<div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
											<div className="sm:col-span-2">
												<Label>Categoria</Label>
												<Select
													value={cmMemoryForm.category}
													onValueChange={(v) => setCmMemoryForm((s) => ({ ...s, category: v }))}
												>
													<SelectTrigger className="mt-1">
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														{CATEGORIAS_CM.map((c) => (
															<SelectItem key={c.value} value={c.value}>
																{c.emoji} {c.label}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
												<p className="mt-1 text-[11px] text-muted-foreground">
													{CATEGORIA_POR_VALOR_CM[cmMemoryForm.category]?.desc}
												</p>
											</div>
											<div className="sm:col-span-2">
												<Label>Título (opcional)</Label>
												<Input
													className="mt-1"
													placeholder="Ex: Alergia a amendoim"
													value={cmMemoryForm.title}
													onChange={(e) => setCmMemoryForm((s) => ({ ...s, title: e.target.value }))}
												/>
											</div>
											<div className="sm:col-span-4">
												<Label>Conteúdo</Label>
												<Textarea
													className="mt-1 min-h-[110px]"
													placeholder="Detalhe o que é importante lembrar sobre este cliente."
													value={cmMemoryForm.content}
													onChange={(e) => setCmMemoryForm((s) => ({ ...s, content: e.target.value }))}
												/>
											</div>
										</div>
										<DialogFooter>
											<Button
												variant="ghost"
												onClick={() => setCmDialogAberto(false)}
												disabled={cmSending}
											>
												Cancelar
											</Button>
											<Button onClick={salvarCm} disabled={cmSending || semContato}>
												{cmSending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
												{cmModoEdicao ? "Salvar alterações" : "Criar memória"}
											</Button>
										</DialogFooter>
									</DialogContent>
								</Dialog>
								</>
							);
						})()}

						{contexto?.cliente && (
							<Bloco titulo="Cliente">
								<div className="flex items-start gap-3">
									<Avatar className="h-11 w-11 shrink-0 rounded-xl border border-border/40">
										{contexto.cliente.fotoUrl ? (
											<AvatarImage src={contexto.cliente.fotoUrl} alt={contexto.cliente.nome} />
										) : null}
										<AvatarFallback className="text-sm font-semibold uppercase tracking-wide bg-slate-100 text-slate-700">
											{contexto.cliente.nome?.slice(0, 2) || (
												<UserCircle className="h-5 w-5" />
											)}
										</AvatarFallback>
									</Avatar>
									<div className="min-w-0 flex-1 space-y-1">
										<div className="flex items-center justify-between gap-2">
											<h4 className="truncate text-sm font-semibold text-foreground">
												{contexto.cliente.nome || "Sem nome"}
											</h4>
											{contexto.ticket.etiquetas?.length > 0 && (
												<div className="flex max-w-[140px] flex-wrap justify-end gap-1">
													{contexto.ticket.etiquetas.slice(0, 2).map((t) => (
														<Badge
															key={t.id}
															style={{ backgroundColor: `${t.color}15`, color: t.color }}
															className="h-5 rounded-full px-2 py-0 text-[10px] font-medium"
														>
															{t.name}
														</Badge>
													))}
													{contexto.ticket.etiquetas.length > 2 && (
														<Badge variant="outline" className="h-5 rounded-full px-2 py-0 text-[10px]">
															+{contexto.ticket.etiquetas.length - 2}
														</Badge>
													)}
												</div>
											)}
										</div>
										<p className="truncate text-xs text-muted-foreground">
											{contexto.cliente.numero || "—"}
											{contexto.cliente.email ? ` · ${contexto.cliente.email}` : ""}
										</p>
										<div className="grid grid-cols-3 gap-2 pt-1 text-center">
											<div className="rounded-lg border bg-slate-50 dark:bg-slate-900/40 p-1.5">
												<div className="text-[10px] uppercase tracking-wide text-slate-500">
													tickets
												</div>
												<div className="text-sm font-semibold tabular-nums">
													{contexto.cliente.totalTickets ?? 0}
												</div>
											</div>
											<div className="rounded-lg border bg-emerald-50 dark:bg-emerald-900/20 p-1.5">
												<div className="text-[10px] uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
													fechados
												</div>
												<div className="text-sm font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
													{contexto.cliente.fechados ?? 0}
												</div>
											</div>
											<div className="rounded-lg border bg-indigo-50 dark:bg-indigo-900/20 p-1.5">
												<div className="text-[10px] uppercase tracking-wide text-indigo-600 dark:text-indigo-400">
													taxa
												</div>
												<div className="text-sm font-semibold tabular-nums text-indigo-700 dark:text-indigo-300">
													{contexto.cliente.taxaResolucao ?? 0}%
												</div>
											</div>
										</div>
										{contexto.cliente.filaPreferida?.name && (
											<p className="pt-1 text-[11px] text-muted-foreground">
												Fila preferida ·{" "}
												<span className="font-medium" style={{ color: contexto.cliente.filaPreferida.color || "inherit" }}>
													{contexto.cliente.filaPreferida.name}
												</span>{" "}
												({contexto.cliente.filaPreferida.n} vezes)
											</p>
										)}
									</div>
								</div>
							</Bloco>
						)}

						{contexto?.quickAnswers?.length > 0 && (
							<Bloco
								titulo="Respostas rápidas"
								actions={
									<Badge variant="outline" className="h-5 rounded-full px-2 py-0 text-[10px]">
										match por palavra-chave
									</Badge>
								}
							>
								<ul className="space-y-2">
									{contexto.quickAnswers.map((qa) => (
										<li
											key={qa.id}
											className="rounded-lg border bg-slate-50/60 p-2.5 dark:bg-slate-900/40"
										>
											<div className="mb-1 flex items-center justify-between gap-2">
												<Badge
													variant="secondary"
													className="h-5 rounded-md px-2 py-0 text-[10px] font-mono"
												>
													/{qa.shortcut}
												</Badge>
												<span className="text-[10px] text-slate-500">
													{qa.hit} palavra(s)
												</span>
											</div>
											<p className="whitespace-pre-line text-[12px] leading-relaxed">
												{qa.message}
											</p>
											{qa.mediaName ? (
												<p className="mt-1 text-[11px] text-sky-700 dark:text-sky-400">
													📎 {qa.mediaName}
												</p>
											) : null}
											<div className="mt-2 flex gap-1">
												<Button
													size="sm"
													className="h-7 text-xs"
													onClick={() => usar(qa.message)}
													disabled={!qa.message}
												>
													Usar
												</Button>
												{qa.message ? (
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7"
														aria-label="Copiar"
														onClick={() => copiar(qa.message)}
													>
														<Copy className="h-3.5 w-3.5" />
													</Button>
												) : null}
											</div>
										</li>
									))}
								</ul>
							</Bloco>
						)}

						{contexto?.historico?.length > 0 && (
							<Bloco titulo="Histórico do cliente">
								<ul className="space-y-1.5">
									{contexto.historico.map((h) => (
										<li
											key={h.id}
											className="flex items-center justify-between gap-2 rounded-md border border-transparent hover:border-slate-200/70 hover:bg-slate-50/60 dark:hover:bg-slate-900/40 px-2 py-1.5"
										>
											<div className="flex items-center gap-2 min-w-0">
												<span
													className={cn(
														"inline-flex h-6 items-center justify-center rounded-md px-1.5 text-[10px] font-medium",
														STATUS_COR[h.status] || "bg-slate-100 text-slate-600"
													)}
												>
													{h.status === "open"
														? "aberto"
														: h.status === "pending"
														? "aguard"
														: h.status === "closed"
														? "fechado"
														: h.status}
												</span>
												{h.fila?.name ? (
													<span
														className="truncate text-[11px] font-medium"
														style={{ color: h.fila.color || "inherit" }}
													>
														{h.fila.name}
													</span>
												) : (
													<span className="text-[11px] text-slate-500">sem fila</span>
												)}
											</div>
											<div className="flex items-center gap-1 shrink-0 text-[10px] text-slate-500">
												<CalendarDays className="h-3 w-3 opacity-70" />
												{fmtData(h.criado)}
											</div>
										</li>
									))}
								</ul>
							</Bloco>
						)}

						{/* PRÓXIMAS AÇÕES + TIMELINE NO FIM */}
						{contexto?.proximasAcoes?.length > 0 && (
							<Bloco
								titulo="Próximas ações"
								actions={
									<Button
										variant="ghost"
										size="icon"
										className="h-7 w-7"
										title="Atualizar"
										onClick={() => carregarContexto(true)}
									>
										{loadingCtx ? (
											<Loader2 className="h-3.5 w-3.5 animate-spin" />
										) : (
											<RefreshCw className="h-3.5 w-3.5" />
										)}
									</Button>
								}
							>
								<ul className="space-y-2">
									{contexto.proximasAcoes.map((a) => {
										const Icone = iconeDoNome(a.icone);
										return (
											<li
												key={a.id}
												className={cn(
													"flex items-start gap-2 rounded-lg border p-2",
													NIVEL_COR[a.nivel] || NIVEL_COR.baixo
												)}
											>
												<span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white/70 dark:bg-black/20">
													<Icone className="h-3.5 w-3.5" />
												</span>
												<div className="min-w-0 flex-1">
													<div className="text-xs font-semibold leading-tight">
														{a.titulo}
													</div>
													{a.descricao && (
														<p className="mt-0.5 text-[11px] leading-relaxed opacity-85">
															{a.descricao}
														</p>
													)}
												</div>
											</li>
										);
									})}
								</ul>
							</Bloco>
						)}

						{contexto?.timeline?.length > 0 && (
							<Bloco titulo="Linha do tempo do ticket">
								<ol className="relative ml-2 space-y-3 border-l border-slate-200 pl-4 dark:border-slate-800">
									{contexto.timeline.map((t) => (
										<li key={t.id} className="relative">
											<span
												className={cn(
													"absolute -left-[21px] top-0.5 h-3 w-3 rounded-full border-2 border-white dark:border-slate-950",
													TIMELINE_COR[t.cor] || "bg-slate-400"
												)}
											/>
											<div className="text-xs font-semibold leading-tight">
												{t.titulo}
											</div>
											<div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
												<span>{fmtData(t.horario)}</span>
												{t.extra ? <span>· {t.extra}</span> : null}
											</div>
										</li>
									))}
								</ol>
							</Bloco>
						)}
					</div>
				</SheetContent>
			</Sheet>
		</>
	);
};

export default AiCopilot;
